import { IsEnum, IsInt, IsNotEmpty, Min } from "class-validator";
import { extendArray } from "../handlers/types";
import { AbstractJSONModel } from "./master-model";
import { eSalerySystem } from "./institution-model";

export enum eCategory {
  Normal = 0,
  Vacation = 1,
  Seniority = 2,
  Illness = 3,
  InLieuTime = 4
}

export enum eSoCalculatesAs {
  NOT_SET = 0,
  PLANNEDWORKHOURS = 1, // default fra 1. august 2023. Alle timer på dagen tæller uanset om der er 2h, 5h eller 12h
  NORMDAY = 2
}

@extendArray
export class AbsenceTypeArray extends Array<AbsenceType> {
  constructor(absenceTypes: AbsenceType[]) {
    super(...absenceTypes);
    Object.setPrototypeOf(this, Object.create(AbsenceTypeArray.prototype));
  }
  absenceTypeByCode(code: string): AbsenceType | null {
    return this.find((c) => c.code === code) ?? null;
  }
}

export class AbsenceTypeDialogProperties {
  code: string = "";
  b: boolean = false;
  c: boolean = false;
  salary_system: string = "";
  same_day: boolean = false;
  open_allowed: boolean = false;
  absencehours: boolean = false;
  absencehours_disabled: boolean = false;
  child_expected_dob: boolean = false;
  child_dob: boolean = false;
  child_dob_disabled: boolean = false;
  from_to_time: boolean = false;
  additional_data: string = "";
  color: boolean = false;
  can_be_copied_to_other: boolean = false;
  can_be_copied_on_employee: boolean = false;
  can_be_reported: boolean = false;
  obsolete: boolean = false;
  changes: boolean = false;
  absence_text: string = "";
  kmd_open_allowed: boolean = false;
  kmd_planned_absence: boolean = false;
  kmd_overwriting_allowed: boolean = false;
  kmd_quota_affected: boolean = false;
  kmd_additional_data: boolean = false;
  kmd_application: string = "";
}

export interface IAbsenceType {
  id: number;
  description: string;
  whole_day: boolean;
  report_to_salary_system: boolean;

  category: eCategory;
  created_at: Date;
  updated_at: Date | null;
  code: string;
  single_day: boolean;
  show_on_top: boolean;
  is_system_defined: boolean;
  is_hidden: boolean;
  institution_id: number;
  dialog_properties: any | null;
}

export interface IAbsenceTypePost {
  institution_id: number;
  description: string;
  whole_day: boolean;
  category: eCategory;
  code: string;
  single_day: boolean;
  show_on_top: boolean;
  is_hidden: boolean;
  report_to_salary_system: boolean;
  is_system_defined: boolean;
}

export interface IAbsenceTypePut {
  description: string;
  category: eCategory;
  code: string;
  is_hidden: boolean;
}

export class AbsenceType extends AbstractJSONModel implements IAbsenceType {
  @IsInt()
  @Min(0)
  public id = -1;
  public external_id = "";
  public description = "";
  public whole_day = false;
  public report_to_salary_system = true;
  @IsNotEmpty()
  @IsEnum(eCategory)
  public category: eCategory = 0;
  public created_at: Date = new Date();
  public updated_at: Date | null = null;
  public code = "";
  public single_day = false;
  public show_on_top = false;
  public is_system_defined = true;
  public is_hidden = false;
  public institution_id = 0;
  public salary_system = eSalerySystem.Manuel;
  public dialog_properties: AbsenceTypeDialogProperties | null = null;
  // frontend only fields
  public nonePlanned = false;

  equals(other: AbsenceType): boolean {
    return other.id === this.id;
  }

  public get has_zero_duration(): boolean {
    return (
      [eCategory.Vacation, eCategory.Seniority, eCategory.InLieuTime].some(
        (a) => a == this.category
      ) && this.whole_day
    );
  }

  private static common: string[] = [
    "AFS",
    "AP",
    "ASB",
    "BA",
    "BD",
    "BG",
    "BH",
    "BU",
    "DS",
    "FF",
    "GG",
    "HJ",
    "KU",
    "NT",
    "OS",
    "OS½",
    "P2",
    "PAS",
    "SO",
    "SY",
    "TJ",
    // For empe user only to see colleagues absence with non sensitive code/description
    "FR",
    "SYG",
    "S.O"
  ];

  private static kMDAndManual: string[] = [
    "AFS½",
    "AP½",
    "BA½",
    "BD½",
    "FE",
    "FE½",
    "FF½",
    "GG½",
    "OR",
    "P2½",
    "PD",
    "PG",
    "PU",
    "SO½",
    "FV",
    "FV½"
  ];
  // default systemcodes based on salery system
  public static salerySystemSD: string[] = AbsenceType.common.concat(["FE", "SY-A"]);
  public static salerySystemManuel: string[] = AbsenceType.common
    .concat(AbsenceType.kMDAndManual)
    .concat(["SY-A"]);
  public static salerySystemKMD: string[] = AbsenceType.common.concat(AbsenceType.kMDAndManual);
  // none planned codes based on salery system
  public static salerySystemSDnoneplanned: string[] = ["SYG", "SY-A", "SY", "HJ", "DS"];
  public static salerySystemManuelnoneplanned: string[] = ["SYG", "SY-A", "SY", "HJ", "DS"];
  public static salerySystemKMDnoneplanned: string[] = ["SYG", "SY", "HJ", "DS"];

  convertToPostObject(): IAbsenceTypePost {
    const at: IAbsenceTypePost = Object.assign({});
    at.institution_id = this.institution_id;
    at.description = this.description;
    at.whole_day = this.whole_day;
    at.category = this.category;
    at.code = this.code;
    at.single_day = this.single_day;
    at.show_on_top = this.show_on_top;
    at.is_hidden = this.is_hidden;
    at.report_to_salary_system = this.report_to_salary_system;
    at.is_system_defined = this.is_system_defined;
    return at;
  }

  convertToPutObject(): IAbsenceTypePut {
    const at: IAbsenceTypePost = Object.assign({});
    at.description = this.description;
    at.category = this.category;
    at.code = this.code;
    at.is_hidden = this.is_hidden;
    return at;
  }
}
